import React from 'react';
import { PropTypes } from 'prop-types';
import Pagination from './Pagination';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

export default {
  title: 'Components/pagination/Pagination',
  component: Pagination,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `Pagination` component provides navigation controls for paginated data. It supports next/previous buttons and a dynamic list of page numbers for intuitive navigation.\n\n',
      // When & how to use it?
      '\nUse the `Pagination` component when data needs to be displayed in a paginated format. It is suitable for large datasets where showing all items at once is impractical.\n\nThe component integrates the `ListOfPages` component for rendering page numbers and includes controls for navigating between pages.\n\n',
      // Current Usage
      `\nThe \`Pagination\` component is widely used in various views across the app, such as:
      \n- VisitsPage
      \n- SitesPage
      \n- NotesPage
      \n- ContactPage
      \n- RoutesPage
      \n- SeasonsPage
      \n- TagsPage etc.

      \n\nIt ensures seamless navigation between data sets displayed across multiple pages.\n\n`,
      // Related Components
      '\n- **[ListOfPages](http://localhost:6006/?path=/docs/components-pagination-listofpages--docs)**: Used for rendering the list of page numbers dynamically.\n\n',
    ),
  },
  tags: ['autodocs'],
};

// Base Template function for stories
const PaginationTemplate = ({ initialPage, totalItems = 20 }) => {
  const [currentPage, setCurrentPage] = React.useState(initialPage);
  return <Pagination itemsPerPage={1} totalItems={totalItems} currentPage={currentPage} setCurrentPage={setCurrentPage} />;
};

// Binding the base template
const Template = PaginationTemplate.bind({});

// Creating stories by applying different props
export const SinglePage = Template.bind({});
SinglePage.args = {
  initialPage: 1,
  totalItems: 1,
};
SinglePage.parameters = generateStoryDocs(
  // When is this variant used?
  '\nUse this variant to display a single-page scenario where no pagination controls are required beyond the current page.\n\n',
  // Where is it currently used on the product?
  '\nThe `SinglePage` variant is used when only one page of data exists, such as small datasets or filtered results.\n\n',
);

export const MultiplePages = Template.bind({});
MultiplePages.args = {
  initialPage: 1,
  totalItems: 4,
};
MultiplePages.parameters = generateStoryDocs(
  // When is this variant used?
  '\nUse this variant to display multiple pages with simple pagination controls for navigation.\n\n',
  // Where is it currently used on the product?
  '\nThe `MultiplePages` variant is used when datasets are split across several pages, typically 3-5 pages.\n\n',
);

export const MultiplePagesShortened = Template.bind({});
MultiplePagesShortened.args = {
  initialPage: 9,
  totalItems: 17,
};
MultiplePagesShortened.parameters = generateStoryDocs(
  // When is this variant used?
  '\nUse this variant for datasets with many pages where compact pagination controls with ellipses are needed to simplify navigation.\n\n',
  // Where is it currently used on the product?
  '\nThe `MultiplePagesShortened` variant is used for large datasets where full page numbers cannot be displayed due to space constraints, and ellipses are introduced.\n\n',
);

PaginationTemplate.propTypes = {
  initialPage: PropTypes.number.isRequired,
  totalItems: PropTypes.number,
};
